import { useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { register } from "../../actions/authActions";
import Navbar from "../../components/Navbar/Navbar";
import { Container, Form, Button, Alert, Spinner } from "react-bootstrap";
import Footer from "../../components/Footer/Footer";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Signup = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [apiSuccess, setApiSuccess] = useState("");

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phone: "",
    address: "",
    password: "",
    confirm_password: "",
    role_id: "b2crole",
  });

  const [errors, setErrors] = useState({
    username: "",
    email: "",
    phone: "",
    address: "",
    password: "",
    confirm_password: "",
    role_id: "",
  });

  const { username, email, phone, address, password, confirm_password, role_id } =
    formData;

  const roleMappings = {
    fleetrole: 2,
    companyrole: 3,
    thirdprole: 4,
    workshoprole: 5,
    b2crole: 6,
  };

  const onChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setApiError("");
    setApiSuccess("");
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const validate = () => {
    const next = {
      username: "",
      email: "",
      phone: "",
      address: "",
      password: "",
      confirm_password: "",
      role_id: "",
    };
    if (!username.trim()) next.username = "Username is required.";
    if (!email.trim()) next.email = "Email is required.";
    else if (!emailRegex.test(email.trim())) next.email = "Enter a valid email.";
    if (!address.trim()) next.address = "Address is required.";
    if (!phone.toString().trim()) next.phone = "Phone is required.";
    if (!role_id) next.role_id = "Role is required.";
    if (!password) next.password = "Password is required.";
    else if (password.length < 6) next.password = "Min 6 characters.";
    if (!confirm_password) next.confirm_password = "Confirm your password.";
    else if (confirm_password !== password) next.confirm_password = "Passwords do not match.";
    setErrors(next);
    return Object.values(next).every((v) => !v);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (submitting) return;
    setSubmitting(true);
    setApiError("");
    setApiSuccess("");

    try {
      const payload = {
        username: username.trim(),
        email: email.trim(),
        phone,
        address: address.trim(),
        password,
        role_id: roleMappings[role_id],
      };
      const result = await dispatch(register(payload));
      if (result?.ok) {
        setApiSuccess("Account created successfully. Redirecting to login…");
        // hand off a success banner via navigation state
        navigate("/login", { replace: true, state: { signupSuccess: true } });
      } else {
        setApiError(result?.error || "Unable to sign up. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Navbar />

      <Container fluid className="d-flex justify-content-center">
        <Form className="form-container p-4" onSubmit={onSubmit} noValidate>
          <h3>Signup</h3>

          {apiError && (
            <Alert variant="danger" className="mt-2" role="alert" aria-live="assertive">
              {apiError}
            </Alert>
          )}
          {apiSuccess && (
            <Alert variant="success" className="mt-2">
              {apiSuccess}
            </Alert>
          )}

          <Form.Group className="form-column">
            <Form.Label>Username</Form.Label>
            <Form.Control
              type="text"
              name="username"
              value={username}
              placeholder="Username"
              onChange={onChange}
              isInvalid={!!errors.username}
              disabled={submitting}
              required
            />
            <Form.Control.Feedback type="invalid">{errors.username}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="form-column">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={email}
              placeholder="Email"
              onChange={onChange}
              isInvalid={!!errors.email}
              disabled={submitting}
              required
            />
            <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="form-column">
            <Form.Label>Address</Form.Label>
            <Form.Control
              type="text"
              name="address"
              value={address}
              placeholder="Address"
              onChange={onChange}
              isInvalid={!!errors.address}
              disabled={submitting}
              required
            />
          </Form.Group>

          <Form.Group className="form-column">
            <Form.Label>Phone</Form.Label>
            <Form.Control
              type="number"
              name="phone"
              value={phone}
              placeholder="Phone"
              onChange={onChange}
              isInvalid={!!errors.phone}
              disabled={submitting}
              required
            />
            <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="form-column">
            <Form.Label>Password</Form.Label>
            <Form.Control
              type="password"
              name="password"
              value={password}
              placeholder="Password"
              onChange={onChange}
              isInvalid={!!errors.password}
              disabled={submitting}
              required
            />
            <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="form-column">
            <Form.Label>Confirm Password</Form.Label>
            <Form.Control
              type="password"
              name="confirm_password"
              value={confirm_password}
              placeholder="Confirm Password"
              onChange={onChange}
              isInvalid={!!errors.confirm_password}
              disabled={submitting}
              required
            />
            <Form.Control.Feedback type="invalid">{errors.confirm_password}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="form-column">
            <Form.Label>Role</Form.Label>
            <Form.Control
              as="select"
              name="role_id"
              value={role_id}
              onChange={onChange}
              isInvalid={!!errors.role_id}
              disabled={submitting}
              required
            >
              <option value="b2c" disabled>
                Select role
              </option>
              <option value="fleetrole">Distributors/Retailers</option>
              <option value="companyrole">Fleets/Corporate Clients</option>
              <option value="thirdprole">3rd Party Suppliers</option>
              <option value="workshoprole">Workshops/Garages</option>
              <option value="b2crole">B2C Customers</option>
            </Form.Control>
            <Form.Control.Feedback type="invalid">{errors.role_id}</Form.Control.Feedback>
          </Form.Group>

          <Button variant="primary" type="submit" className="login-button d-inline-flex align-items-center gap-2" disabled={submitting}>
            {submitting && <Spinner animation="border" size="sm" />}
            {submitting ? "Submitting…" : "Signup"}
          </Button>

          <div className="mt-3">
            Do you have an account?
            <Link to={"/login"}>
              <span> Login here</span>
            </Link>
          </div>
        </Form>
      </Container>

      <Footer />
    </div>
  );
};

export default Signup;
